import { IShuttleAddedJourney, IShuttleStopOption } from "../../Orchestrator/Types/Interface/IShuttleBus";
import { RideHailingMode } from "../../Orchestrator/Types/Interface/IRideHailing";

const SHUTTLE_SUBSTATION_TYPE = "bus";

interface MinimalSubStation {
    id: string;
    name?: string;
    type?: string;
}

interface MinimalStation {
    id: string;
    name?: string;
    cityName?: string;
    subStations?: MinimalSubStation[] | null;
}

/**
 * Shuttle stops for a station = that station's `subStations` of type "bus".
 * `stationId` is the train station's stopPlaceRef (== the station entry's `id`, the
 * externalCodes-mapped code). Each returned `stopId` is the shuttle's mapped code,
 * which is used directly as the offers `stopPlaceRef`.
 */
export function getShuttleStopOptions(
    stations: MinimalStation[],
    stationId: string
): IShuttleStopOption[] {
    if (!stationId) return [];
    const station = (stations || []).find((s) => s.id === stationId);
    if (!station || !Array.isArray(station.subStations)) return [];
    return station.subStations
        .filter((sub) => (sub?.type || "").toLowerCase() === SHUTTLE_SUBSTATION_TYPE)
        .map((sub) => ({ stopId: sub.id, stopName: sub.name || sub.id }));
}

export function hasShuttleStops(stations: MinimalStation[], stationId: string): boolean {
    return getShuttleStopOptions(stations, stationId).length > 0;
}

export interface NormalizedBookedShuttle {
    offerId: string;
    stopId: string;
    originRef: string;
    destinationRef: string;
    /** Localized name of the offer's origin endpoint. */
    originName: string;
    /** Localized name of the offer's destination endpoint. */
    destinationName: string;
    /** Non-station endpoint name (kept for back-compat / simple callers). */
    stopName: string;
    /** Station endpoint name (kept for back-compat / simple callers). */
    stationName: string;
    depTime: string;
    arrTime: string;
    /** Full ISO departure, kept so callers can compare the shuttle's DATE. */
    depDateTime?: string;
    trainNumber: string;
    durationLabel: string;
    totalPrice: number;
    passengerCount: number;
    /** Riders' externalRefs — pre-checks the passenger list on edit. */
    passengerRefs?: string[];
}

/**
 * Map booked shuttle offers to journey cards per side, comparing each offer's
 * origin/destination stop ref against the train leg's origin/destination.
 */
export function mapBookedShuttlesToSides(
    bookedShuttles: NormalizedBookedShuttle[],
    trainOriginRef: string,
    trainDestinationRef: string
): Partial<Record<RideHailingMode, IShuttleAddedJourney>> {
    const result: Partial<Record<RideHailingMode, IShuttleAddedJourney>> = {};
    for (const s of bookedShuttles || []) {
        const isToStation = s.destinationRef === trainOriginRef;
        const isFromStation = s.originRef === trainDestinationRef;
        const side: RideHailingMode | null = isToStation ? "toStation" : isFromStation ? "fromStation" : null;
        if (!side) continue;
        // toStation: stop = offer origin, station = offer destination (the train's origin station).
        // fromStation: station = offer origin (the train's destination station), stop = offer destination.
        const stopName = isToStation ? (s.originName || s.stopName) : (s.destinationName || s.stopName);
        const stationName = isToStation ? (s.destinationName || s.stationName) : (s.originName || s.stationName);
        // Last-in-array wins when a side holds two offers, and that is
        // deliberate: nothing in the payload can rank them. Ranking by recency
        // was tried and reverted — `admission.createdOn` and `confirmableUntil`
        // arrive identical on every offer of the booking (verified live on
        // LLUII6, where two shuttles booked 50 minutes apart both read
        // 00:47:04.302727Z), so it is the booking's timestamp, not the offer's.
        //
        // The ambiguous case — a shuttle just refunded, still non-terminal, next
        // to its replacement — is therefore resolved BEFORE this function, by
        // the caller excluding what it refunded (refundedShuttleOfferIdsAtom).
        result[side] = {
            side,
            offerId: s.offerId,
            stopId: side === "toStation" ? s.originRef : s.destinationRef,
            stopName,
            stationName,
            depTime: s.depTime,
            arrTime: s.arrTime,
            depDateTime: s.depDateTime,
            trainNumber: s.trainNumber,
            durationLabel: s.durationLabel,
            totalPrice: s.totalPrice,
            passengerCount: s.passengerCount,
            passengerRefs: s.passengerRefs,
        };
    }
    return result;
}
